import { type AgentTool, createToolHelper } from '@kapaai/agent-react';
import {
  IconClock,
  IconEye,
  IconRocket,
  IconSettings,
  IconWorldWww,
} from '@tabler/icons-react';
import { type ToolContext } from 'lib/agent/tools';
import { captureEvent } from 'lib/analytics/capture-event';
import { z } from 'zod';

const tool = createToolHelper<ToolContext>();
const JSON_HEADERS = { 'Content-Type': 'application/json' };
const PREVIEW_WAIT_MS = 30_000;
const PREVIEW_POLL_MS = 2_000;

interface SourceResponse {
  id: string;
}

interface ConfigResponse {
  id: string;
}

interface PreviewPage {
  id: string;
  url: string;
  content_type: string | null;
}

interface SkippedPage {
  id: string;
  url: string;
  status_code: number | null;
  skip_reason: string;
  skip_reason_label: string;
}

interface PreviewStatus {
  status: 'IDLE' | 'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE';
  pages_accepted?: number;
  total_skipped?: number;
  skip_categories?: Array<{
    key: string;
    label: string;
    description: string;
    severity: string;
    count: number;
  }>;
  preview_page_limit?: number | null;
  was_preview_capped?: boolean;
  error_message?: string | null;
  is_user_actionable?: boolean | null;
}

const sleep = (milliseconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const headAndTail = (value: string, limit: number) => {
  if (value.length <= limit) return value;
  const half = Math.floor(limit / 2);
  return `${value.slice(0, half)}\n\n... middle omitted ...\n\n${value.slice(-half)}`;
};

const responseJson = async <T>(response: Response, operation: string) => {
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`${operation} failed (${response.status}): ${detail}`);
  }
  return (await response.json()) as T;
};

interface SourceDetail {
  name: string;
  deployed_markdown_items: number;
  ready_for_review: boolean;
}

// Mirrors hasPublishedSourceItems in features/sources: a source with published
// items serves live answers, so config changes to it need user approval.
const isSourceLive = (source: SourceDetail) =>
  source.deployed_markdown_items > 0 || source.ready_for_review;

const readSourceDetail = async (
  authFetch: ToolContext['authFetch'],
  projectId: string,
  sourceId: string,
) =>
  responseJson<SourceDetail>(
    await authFetch(
      `/ingestion/v1/internal/projects/${projectId}/sources/${sourceId}/`,
    ),
    'Reading webcrawl source',
  );

const fetchPageSamples = async <T>(
  authFetch: ToolContext['authFetch'],
  path: string,
  operation: string,
) => {
  const pageSize = 100;
  const first = await responseJson<{ count: number; results: T[] }>(
    await authFetch(`${path}&page=1&page_size=${pageSize}`),
    operation,
  );
  if (first.count <= pageSize) {
    return { ...first, omitted: 0, sampled: false };
  }
  const lastPage = Math.ceil(first.count / pageSize);
  const last = await responseJson<{ count: number; results: T[] }>(
    await authFetch(`${path}&page=${lastPage}&page_size=${pageSize}`),
    operation,
  );
  const results = [...first.results, ...last.results];
  return {
    count: first.count,
    results,
    omitted: first.count - results.length,
    sampled: true,
  };
};

const waitForPreview = async (
  authFetch: ToolContext['authFetch'],
  sourceId: string,
) => {
  const deadline = Date.now() + PREVIEW_WAIT_MS;
  const readStatus = async () =>
    responseJson<PreviewStatus>(
      await authFetch(
        `/ingestion/v1/source-scrapes/${sourceId}/crawl-task-status/?preview=true`,
      ),
      'Reading webcrawl status',
    );
  let status = await readStatus();
  while (
    status.status !== 'SUCCESS' &&
    status.status !== 'FAILURE' &&
    Date.now() < deadline
  ) {
    await sleep(PREVIEW_POLL_MS);
    status = await readStatus();
  }
  return status;
};

/** What the agent just did to a source, so the app can react to it. */
export type SourceChange = 'created' | 'updated' | 'deployed';

export interface WebcrawlSetupToolOptions {
  /** Tools run outside React; this is how they report a changed source back. */
  onSourceChanged?: (sourceId: string, change: SourceChange) => void;
}

const crawlConfigFields = {
  urls_start: z.array(z.string().url()).optional(),
  urls_include: z.array(z.string()).optional(),
  interpret_urls_include_as_regex: z.boolean().optional(),
  urls_exclude: z.array(z.string()).optional(),
  interpret_urls_exclude_as_regex: z.boolean().optional(),
  urls_exclude_download: z.array(z.string()).optional(),
  interpret_urls_exclude_download_as_regex: z.boolean().optional(),
  extensions_exclude: z.array(z.string()).optional(),
  additional_supported_file_types: z.array(z.enum(['pdf'])).optional(),
  enable_sitemap: z.boolean().optional(),
  sitemap_urls: z.array(z.string().url()).nullable().optional(),
  render_js: z.boolean().optional(),
  use_proxy: z.boolean().optional(),
  drop_params: z.boolean().optional(),
  click_selector: z.string().optional(),
  load_more_selector: z.string().optional(),
  minimum_date_published: z
    .string()
    .describe('ISO 8601 publication cutoff for pages to include')
    .nullable()
    .optional(),
  html_selectors_include: z
    .array(z.string())
    .describe(
      'Crawl-time page filter: only pages containing one of these selectors are kept. Not for content cleanup; use the extraction selectors for that',
    )
    .optional(),
  html_selectors_exclude: z
    .array(z.string())
    .describe(
      'Crawl-time page filter: pages containing one of these selectors are skipped entirely. Not for content cleanup; use the extraction selectors for that',
    )
    .optional(),
  wait_for_selectors: z.array(z.string()).optional(),
};

const convertConfigFields = {
  selector: z
    .string()
    .describe('CSS or XPath selector for the primary content'),
  selectors_exclude: z
    .array(z.string())
    .describe('CSS selectors to remove from the selected content')
    .optional(),
  classes_exclude: z
    .array(z.string())
    .describe('Bare class names to remove, without a leading dot')
    .optional(),
  wrapper_classes_exclude: z
    .array(z.string())
    .describe(
      'Bare wrapper class names to unwrap while retaining their children',
    )
    .optional(),
};

interface CrawlConfigUpdate {
  source_id: string;
  preview_page_limit: number | null;
  [field: string]: unknown;
}

async function applyCrawlConfigUpdate(
  { source_id, preview_page_limit, ...updates }: CrawlConfigUpdate,
  authFetch: ToolContext['authFetch'],
) {
  const patch = Object.fromEntries(
    Object.entries(updates).filter(([, value]) => value !== undefined),
  );
  if (Object.keys(patch).length === 0) {
    throw new Error('At least one webcrawl setting must be changed');
  }
  // The config id comes from the source itself, so an update can only ever
  // touch this source's configuration, whatever ids the model has around.
  const current = await responseJson<{
    config: { id: string } & Record<string, unknown>;
  }>(
    await authFetch(`/ingestion/v1/source-scrapes/${source_id}/crawl-config/`),
    'Reading current webcrawl configuration',
  );
  const configId = current.config.id;
  const rollback = Object.fromEntries(
    Object.keys(patch).map((field) => [field, current.config[field]]),
  );
  // The backend rejects the save (409) while a real crawl is running or
  // approved pages are still being processed. Nothing on this side can clear
  // that, so the error is passed on for the agent to tell the user to wait.
  await responseJson(
    await authFetch(`/ingestion/v1/crawl-configs/${configId}/`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(patch),
    }),
    'Updating webcrawl configuration',
  );
  try {
    await responseJson(
      await authFetch(
        `/ingestion/v1/source-scrapes/${source_id}/crawl-task-schedule/`,
        {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ page_limit: preview_page_limit }),
        },
      ),
      'Starting updated webcrawl preview',
    );
  } catch (error) {
    await responseJson(
      await authFetch(`/ingestion/v1/crawl-configs/${configId}/`, {
        method: 'PATCH',
        headers: JSON_HEADERS,
        body: JSON.stringify(rollback),
      }),
      'Rolling back webcrawl configuration',
    );
    throw error;
  }
  return {
    source_id,
    crawl_config_id: configId,
    preview_status: 'PENDING',
    updates: patch,
  };
}

const configurationTools = (
  options: WebcrawlSetupToolOptions,
): AgentTool<ToolContext>[] => [
  tool({
    name: 'list_sources',
    description:
      "List the project's knowledge sources: id, name, type, and whether each is deployed. Use it to find the source the user is talking about before inspecting or changing it.",
    parameters: z.object({
      page: z.number().int().positive().optional(),
    }),
    displayName: 'List Sources',
    icon: IconEye,
    iconColor: '#38bdf8',
    execute: async ({ page }, { authFetch, projectId }) => {
      const sources = await responseJson<{
        count: number;
        results: Array<{
          id: string;
          name: string;
          type: string;
          deployed_markdown_items: number;
          ready_for_review: boolean;
        }>;
      }>(
        await authFetch(
          `/ingestion/v1/internal/projects/${projectId}/sources/?page=${page ?? 1}&page_size=50`,
        ),
        'Listing sources',
      );
      return {
        total_sources: sources.count,
        sources: sources.results.map((source) => ({
          id: source.id,
          name: source.name,
          type: source.type,
          is_deployed: isSourceLive(source),
        })),
      };
    },
  }),

  tool({
    name: 'get_webcrawl_config',
    description:
      "Read a webcrawl source's current configuration: the crawl settings (URL rules, sitemap, JavaScript, page filters) and the content-extraction settings (selector and exclusions). Always read this before changing an existing source, so updates build on what is actually configured.",
    parameters: z.object({
      source_id: z.string().uuid(),
    }),
    displayName: 'Read Webcrawl Configuration',
    icon: IconSettings,
    iconColor: '#38bdf8',
    execute: async ({ source_id }, { authFetch }) => {
      const crawl = await responseJson<{ config: unknown }>(
        await authFetch(
          `/ingestion/v1/source-scrapes/${source_id}/crawl-config/`,
        ),
        'Reading crawl configuration',
      );
      const convert = await responseJson<{ config: unknown }>(
        await authFetch(
          `/ingestion/v1/source-scrapes/${source_id}/convert-config/`,
        ),
        'Reading content-extraction configuration',
      );
      return { crawl_config: crawl.config, convert_config: convert.config };
    },
  }),

  tool({
    name: 'create_webcrawl',
    description:
      'Create a draft webcrawl source, configure discovery, and start a preview. After this returns, always call get_webcrawl_preview until the preview succeeds, then inspect its URLs and content before deploying.',
    parameters: z.object({
      name: z
        .string()
        .describe('Human-readable source name inferred from the site'),
      ...crawlConfigFields,
      urls_start: z.array(z.string().url()).min(1),
      preview_page_limit: z.number().int().positive().nullable().optional(),
    }),
    displayName: 'Create Webcrawl',
    icon: IconWorldWww,
    iconColor: '#22d3ee',
    execute: async (
      { name, preview_page_limit, ...requestedConfig },
      { authFetch, projectId },
    ) => {
      const source = await responseJson<SourceResponse>(
        await authFetch('/ingestion/v1/source-scrapes/', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ project: projectId, name }),
        }),
        'Creating webcrawl source',
      );
      captureEvent('source_created', { source_type: 'scrape', via: 'agent' });
      options.onSourceChanged?.(source.id, 'created');

      let config: ConfigResponse;
      try {
        config = await responseJson<ConfigResponse>(
          await authFetch('/ingestion/v1/crawl-configs/', {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify({
              source_scrape: source.id,
              ...requestedConfig,
              urls_include: requestedConfig.urls_include ?? [],
              urls_exclude: requestedConfig.urls_exclude ?? [],
              enable_sitemap:
                requestedConfig.enable_sitemap ??
                requestedConfig.urls_start.length === 1,
              sitemap_urls: requestedConfig.sitemap_urls ?? null,
              render_js: requestedConfig.render_js ?? false,
              use_proxy: requestedConfig.use_proxy ?? false,
              drop_params: requestedConfig.drop_params ?? true,
              additional_supported_file_types:
                requestedConfig.additional_supported_file_types ?? [],
            }),
          }),
          'Creating webcrawl configuration',
        );
        await responseJson(
          await authFetch(
            `/ingestion/v1/source-scrapes/${source.id}/crawl-task-schedule/`,
            {
              method: 'POST',
              headers: JSON_HEADERS,
              body: JSON.stringify({
                page_limit:
                  preview_page_limit === undefined ? 50 : preview_page_limit,
              }),
            },
          ),
          'Starting webcrawl preview',
        );
      } catch (error) {
        const cleanup = await authFetch(
          `/ingestion/v1/internal/projects/${projectId}/sources/${source.id}/`,
          { method: 'DELETE' },
        );
        if (!cleanup.ok) {
          throw new Error(
            `Webcrawl setup failed and draft ${source.id} could not be removed`,
            { cause: error },
          );
        }
        throw error;
      }
      return {
        source_id: source.id,
        crawl_config_id: config.id,
        name,
        preview_status: 'PENDING',
      };
    },
  }),

  tool({
    name: 'update_webcrawl_config',
    description:
      'Update a draft webcrawl after judging preview results, then start a fresh preview. Use for URL scope, sitemap, JavaScript, proxy, file type, or page-level selector changes. Omitted fields are left unchanged; to clear a list setting, pass an explicit empty array. After this returns, call get_webcrawl_preview again and re-evaluate.',
    parameters: z.object({
      source_id: z.string().uuid(),
      ...crawlConfigFields,
      preview_page_limit: z
        .number()
        .int()
        .positive()
        .nullable()
        .describe('Reuse the page limit reported by the previous preview'),
    }),
    displayName: 'Refine Webcrawl',
    icon: IconSettings,
    iconColor: '#fb923c',
    execute: async (
      { source_id, preview_page_limit, ...updates },
      { authFetch, projectId },
    ) => {
      const source = await readSourceDetail(authFetch, projectId, source_id);
      if (isSourceLive(source)) {
        throw new Error(
          'This source is already deployed. Use update_live_webcrawl_config, which asks the user to approve the change.',
        );
      }
      const result = await applyCrawlConfigUpdate(
        { source_id, preview_page_limit, ...updates },
        authFetch,
      );
      options.onSourceChanged?.(source_id, 'updated');
      return result;
    },
  }),

  tool({
    name: 'update_live_webcrawl_config',
    description:
      'Update the crawl configuration of a source that is already deployed and serving answers, then start a fresh preview. The change affects what the next scheduled crawl ingests, so it requires user approval; put a plain-language description of the change in change_summary. Omitted fields are left unchanged; to clear a list setting, pass an explicit empty array. For draft sources use update_webcrawl_config instead.',
    parameters: z.object({
      source_id: z.string().uuid(),
      change_summary: z
        .string()
        .describe(
          'Plain-language description of the change, shown to the user on the approval card',
        ),
      ...crawlConfigFields,
      preview_page_limit: z
        .number()
        .int()
        .positive()
        .nullable()
        .describe('Reuse the page limit reported by the previous preview'),
    }),
    displayName: 'Update Live Webcrawl',
    icon: IconSettings,
    iconColor: '#f87171',
    needsApproval: true,
    execute: async (
      { source_id, preview_page_limit, ...updates },
      { authFetch, projectId },
    ) => {
      const { change_summary: _changeSummary, ...configUpdates } = updates;
      // The config endpoints are not project scoped; reading the source
      // through the project confirms it belongs here before anything changes.
      await readSourceDetail(authFetch, projectId, source_id);
      const result = await applyCrawlConfigUpdate(
        { source_id, preview_page_limit, ...configUpdates },
        authFetch,
      );
      options.onSourceChanged?.(source_id, 'updated');
      return result;
    },
  }),
];

const inspectionTools = (): AgentTool<ToolContext>[] => [
  tool({
    name: 'wait',
    description:
      'Pause for up to 30 seconds, for example while a crawl preview is still running, before checking again.',
    parameters: z.object({
      seconds: z.number().int().min(1).max(30),
    }),
    displayName: 'Wait',
    icon: IconClock,
    iconColor: '#94a3b8',
    execute: async ({ seconds }) => {
      await sleep(seconds * 1000);
      return { waited_seconds: seconds };
    },
  }),

  tool({
    name: 'get_webcrawl_preview',
    description:
      'Wait briefly for a webcrawl preview and return its accepted URLs, skipped-page breakdown, and crawl metrics. If still pending, call this again. On success, judge URL scope and use update_webcrawl_config if irrelevant sections were included or expected sections were missed.',
    parameters: z.object({
      source_id: z.string().uuid(),
    }),
    displayName: 'Inspect Webcrawl Preview',
    icon: IconEye,
    iconColor: '#a78bfa',
    execute: async ({ source_id }, { authFetch }) => {
      const status = await waitForPreview(authFetch, source_id);
      if (status.status === 'FAILURE') {
        throw new Error(status.error_message ?? 'Webcrawl preview failed');
      }
      // A crawl still running has only part of its pages, so listing them
      // would let the agent judge scope from an incomplete picture.
      if (status.status !== 'SUCCESS') {
        return { ...status, pages: [], skipped_pages: [] };
      }
      const pages = await fetchPageSamples<PreviewPage>(
        authFetch,
        `/ingestion/v1/webpages/urls/?source_scrape=${source_id}`,
        'Reading webcrawl pages',
      );
      const skippedPages = await fetchPageSamples<SkippedPage>(
        authFetch,
        `/ingestion/v1/webpages/skipped-urls/?source_scrape=${source_id}&preview=true`,
        'Reading skipped webcrawl pages',
      );
      return {
        ...status,
        page_count: pages.count,
        pages: pages.results,
        pages_are_sampled: pages.sampled,
        pages_omitted: pages.omitted,
        skipped_page_count: skippedPages.count,
        skipped_pages: skippedPages.results,
        skipped_pages_are_sampled: skippedPages.sampled,
        skipped_pages_omitted: skippedPages.omitted,
      };
    },
  }),

  tool({
    name: 'detect_webcrawl_config',
    description:
      'Detect content-extraction settings for a source from its preview pages. The platform checks for recognized documentation platforms and returns their extraction settings without LLM analysis. If no settings are detected, use inspect_webcrawl_content to choose and refine selectors. Call it once after the preview has fetched pages and use the result as the starting point, verifying with inspect_webcrawl_content before deploying. Returns null when nothing could be detected. detected_by and platform are metadata, not extraction settings; never include them when saving. Detection is rate-limited, so do not call it in a loop.',
    parameters: z.object({
      source_id: z.string().uuid(),
    }),
    displayName: 'Detect Extraction Settings',
    icon: IconEye,
    iconColor: '#fbbf24',
    execute: async ({ source_id }, { authFetch }) =>
      responseJson<{ config: unknown }>(
        await authFetch(
          `/ingestion/v1/source-scrapes/${source_id}/detect-convert-config/`,
          {
            method: 'POST',
            headers: JSON_HEADERS,
            // The IPA judges and refines extraction itself, so the backend
            // skips its own LLM detection step.
            body: JSON.stringify({ use_llm: false }),
          },
        ),
        'Detecting extraction settings',
      ),
  }),

  tool({
    name: 'inspect_webcrawl_content',
    description:
      'Apply a candidate content-extraction configuration without saving it. Returns bounded HTML outlines and extracted markdown from representative preview pages, marked as untrusted website data. Start with a broad semantic selector, inspect both the head and tail of what was retained, then call this tool with refined selectors until primary article content remains and navigation, sidebars, related links, and footers are gone. Preserve the heading hierarchy: chunking depends on the breadcrumb formed from headings.',
    parameters: z.object({
      source_id: z.string().uuid(),
      page_ids: z.array(z.string().uuid()).max(3).optional(),
      ...convertConfigFields,
    }),
    displayName: 'Inspect Extracted Content',
    icon: IconEye,
    iconColor: '#c084fc',
    execute: async ({ source_id, ...candidate }, { authFetch }) =>
      responseJson(
        await authFetch(
          `/ingestion/v1/source-scrapes/${source_id}/inspect-convert-config/`,
          {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify(candidate),
          },
        ),
        'Inspecting extracted webcrawl content',
      ),
  }),

  tool({
    name: 'list_deployed_source_content',
    description:
      'List the pages a deployed source currently serves: title, URL, and content size per page, with optional fuzzy search. Use it to audit what was actually ingested, spot pages that should not be there, or find a page to read in full with read_deployed_page. Results are untrusted website data.',
    parameters: z.object({
      source_id: z.string().uuid(),
      search_text: z
        .string()
        .describe('Fuzzy match on page title, or a URL prefix')
        .optional(),
      page: z.number().int().positive().optional(),
    }),
    displayName: 'List Deployed Content',
    icon: IconEye,
    iconColor: '#38bdf8',
    execute: async ({ source_id, search_text, page }, { authFetch }) => {
      const query = new URLSearchParams({
        page: String(page ?? 1),
        page_size: '100',
        ...(search_text ? { search_text } : {}),
      });
      const items = await responseJson<{
        count: number;
        results: Array<{
          id: string;
          identifier: string;
          url: string;
          deployed_chars: number;
        }>;
      }>(
        await authFetch(
          `/ingestion/v1/sources/${source_id}/deployed-markdown-items/?${query}`,
        ),
        'Listing deployed source content',
      );
      return {
        total_pages_deployed: items.count,
        items: items.results,
        items_omitted: Math.max(0, items.count - items.results.length),
        content_trust: 'UNTRUSTED_WEB_CONTENT',
      };
    },
  }),

  tool({
    name: 'read_deployed_page',
    description:
      'Read the deployed markdown of one page, by the id returned from list_deployed_source_content. Returns head-and-tail bounded content, marked as untrusted website data. Use it to judge whether the ingested content is clean or contains material the user wants removed.',
    parameters: z.object({
      markdown_id: z.string().uuid(),
    }),
    displayName: 'Read Deployed Page',
    icon: IconEye,
    iconColor: '#38bdf8',
    execute: async ({ markdown_id }, { authFetch }) => {
      const item = await responseJson<{
        id: string;
        identifier: string;
        url: string;
        deployed_chars: number;
        markdown_content: string | null;
      }>(
        await authFetch(`/ingestion/v1/markdown/${markdown_id}/`),
        'Reading deployed page content',
      );
      return {
        ...item,
        markdown_content: headAndTail(item.markdown_content ?? '', 8_000),
        content_trust: 'UNTRUSTED_WEB_CONTENT',
      };
    },
  }),
];

const deploymentTools = (
  options: WebcrawlSetupToolOptions,
): AgentTool<ToolContext>[] => [
  tool({
    name: 'deploy_webcrawl',
    description:
      'Save the content-extraction configuration and deploy the webcrawl. Use it both for the first deployment and to re-deploy an already-live source with changed extraction settings; extraction changes only take effect through a re-deploy, which re-ingests the pages. Call only after preview URL scope and extracted content have both been inspected and judged good. The response lists sampled pages that extracted no content, if any; tell the user and refine afterwards if it matters. This is a user-confirmed step.',
    parameters: z.object({
      source_id: z.string().uuid(),
      ...convertConfigFields,
    }),
    displayName: 'Deploy Webcrawl',
    icon: IconRocket,
    iconColor: '#22c55e',
    needsApproval: true,
    execute: async ({ source_id, ...candidate }, { authFetch, projectId }) => {
      // Omitted lists mean "none" for inspection, but PATCH would leave the
      // saved lists untouched, so complete the config once and use it for both.
      const config = {
        selectors_exclude: [],
        classes_exclude: [],
        wrapper_classes_exclude: [],
        ...candidate,
      };
      const source = await readSourceDetail(authFetch, projectId, source_id);
      const preview = await responseJson<PreviewStatus>(
        await authFetch(
          `/ingestion/v1/source-scrapes/${source_id}/crawl-task-status/?preview=true`,
        ),
        'Checking final webcrawl preview',
      );
      if (preview.status !== 'SUCCESS') {
        throw new Error('The webcrawl preview must complete before deployment');
      }
      const inspectionResponse = await authFetch(
        `/ingestion/v1/source-scrapes/${source_id}/inspect-convert-config/`,
        { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(config) },
      );
      // Successful previews count accepted pages of all file types; inspection
      // returns 409 when none are HTML.
      let pagesWithoutContent: Array<{ id: string; url: string }> = [];
      if (inspectionResponse.status === 409 && !preview.pages_accepted) {
        throw new Error(
          'The webcrawl preview accepted no pages, so there is nothing to deploy. Adjust the crawl settings first.',
        );
      }
      if (inspectionResponse.status !== 409) {
        const inspection = await responseJson<{
          pages: Array<{ id: string; url: string; markdown: string }>;
        }>(inspectionResponse, 'Re-inspecting final content configuration');
        // Report empty samples without blocking layouts that extract correctly.
        pagesWithoutContent = inspection.pages
          .filter((page) => page.markdown.trim().length === 0)
          .map(({ id, url }) => ({ id, url }));
      }
      const current = await responseJson<{ config: ConfigResponse | null }>(
        await authFetch(
          `/ingestion/v1/source-scrapes/${source_id}/convert-config/`,
        ),
        'Reading content configuration',
      );
      const configResponse = current.config
        ? await authFetch(
            `/ingestion/v1/convert-configs/${current.config.id}/`,
            {
              method: 'PATCH',
              headers: JSON_HEADERS,
              body: JSON.stringify(config),
            },
          )
        : await authFetch('/ingestion/v1/convert-configs/', {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify({ source_scrape: source_id, ...config }),
          });
      await responseJson(configResponse, 'Saving content configuration');
      await responseJson(
        await authFetch(
          `/ingestion/v1/source-scrapes/${source_id}/schedule-and-ingest/`,
          { method: 'POST' },
        ),
        'Deploying webcrawl',
      );
      captureEvent('source_deployed', {
        source_type: 'scrape',
        is_redeploy: isSourceLive(source),
        via: 'agent',
      });
      options.onSourceChanged?.(source_id, 'deployed');
      return {
        source_id,
        source_name: source.name,
        deployment_status: 'SCHEDULED',
        pages_without_content: pagesWithoutContent,
      };
    },
  }),
];

export const createWebcrawlSetupTools = (
  options: WebcrawlSetupToolOptions = {},
): AgentTool<ToolContext>[] => [
  ...configurationTools(options),
  ...inspectionTools(),
  ...deploymentTools(options),
];
